import { useEffect, useState } from "react";
import { ChevronDown } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { api, errorMessage, put } from "@/lib/api";
import { loginFieldLocked } from "@/utils/loginContactLock";
import "./OwnContactForm.css";

type ContactRecord = {
  email: string;
  phone: string;
  canLogin: boolean;
  emailEditable?: boolean;
  phoneEditable?: boolean;
  loginLock?: "owner" | "edit" | "scope" | null;
  street: string;
  city: string;
  state: string;
  zip: string;
};

const EMPTY_CONTACT = { email: "", phone: "", street: "", city: "", state: "", zip: "" };

const FIELDS = [
  ["email", "Email", "email"],
  ["phone", "Phone", "tel"],
  ["street", "Street address", "text"],
  ["city", "City", "text"],
  ["state", "State", "text"],
  ["zip", "ZIP", "text"],
] as const;

type FieldId = (typeof FIELDS)[number][0];

export type ContactValues = Record<FieldId, string>;

function ContactField({
  id,
  label,
  type,
  value,
  disabled,
  idPrefix,
  onChange,
}: {
  id: FieldId;
  label: string;
  type: string;
  value: string;
  disabled?: boolean;
  idPrefix: string;
  onChange: (id: FieldId, value: string) => void;
}) {
  const fieldId = `${idPrefix}-${id}`;
  return (
    <label className="al-account-contact-field" htmlFor={fieldId}>
      <span className="al-account-contact-label">{label}</span>
      <input
        id={fieldId}
        className="al-account-contact-input"
        type={type}
        value={value}
        disabled={disabled}
        onChange={(event) => onChange(id, event.target.value)}
      />
    </label>
  );
}

export function ContactFields({
  values,
  disabled,
  locked,
  idPrefix = "al-contact",
  onChange,
}: {
  values: ContactValues;
  disabled?: boolean;
  locked?: (id: "email" | "phone") => boolean;
  idPrefix?: string;
  onChange: (id: FieldId, value: string) => void;
}) {
  const wide = FIELDS.filter(([id]) => id === "email" || id === "phone" || id === "street");
  const row = FIELDS.filter(([id]) => id === "city" || id === "state" || id === "zip");
  const render = ([id, label, type]: (typeof FIELDS)[number]) => (
    <ContactField
      key={id}
      id={id}
      label={label}
      type={type}
      value={values[id]}
      disabled={disabled || ((id === "email" || id === "phone") && Boolean(locked?.(id)))}
      idPrefix={idPrefix}
      onChange={onChange}
    />
  );
  return (
    <>
      {wide.map(render)}
      <div className="al-account-contact-row">{row.map(render)}</div>
    </>
  );
}

function valuesFrom(row: ContactRecord): ContactValues {
  return {
    email: row.email || "",
    phone: row.phone || "",
    street: row.street || "",
    city: row.city || "",
    state: row.state || "",
    zip: row.zip || "",
  };
}

export function ContactDetailsButton({ open, onClick }: { open: boolean; onClick: () => void }) {
  return (
    <button type="button" className="al-account-details" aria-expanded={open} onClick={onClick}>
      View Contact Details
      <ChevronDown size={11} aria-hidden />
    </button>
  );
}

/** Signed-in person's own contact. Stays collapsed until `open` is true. */
export default function OwnContactForm({ open }: { open: boolean }) {
  const { capabilities } = useAuth();
  const canUpdateOwn = Boolean(capabilities?.updateOwnContact);
  const [record, setRecord] = useState<ContactRecord | null>(null);
  const [values, setValues] = useState(EMPTY_CONTACT);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [saved, setSaved] = useState("");

  useEffect(() => {
    if (!open || !canUpdateOwn || record) return;
    let cancelled = false;
    setLoading(true);
    setError("");
    api<{ success: boolean; data: ContactRecord }>("/api/profile/contact")
      .then((env) => {
        if (cancelled || !env.data) return;
        setRecord(env.data);
        setValues(valuesFrom(env.data));
      })
      .catch((err) => {
        if (!cancelled) setError(errorMessage(err));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, canUpdateOwn, record]);

  if (!canUpdateOwn) return null;

  const dirty = Boolean(record) && FIELDS.some(([id]) => values[id] !== (record?.[id] || ""));
  const loginLocked =
    loginFieldLocked(record, "email", capabilities) || loginFieldLocked(record, "phone", capabilities);

  return (
    <div className={`al-account-contact-slot${open ? " is-open" : ""}`} {...(open ? {} : { inert: true })} aria-hidden={!open}>
      <form
        id="al-own-contact"
        className="al-account-contact"
        onSubmit={(event) => {
          event.preventDefault();
          if (!record || saving || loading || !dirty) return;
          setSaving(true);
          setError("");
          setSaved("");
          put<{ success: boolean; data: ContactRecord }>("/api/profile/contact", values)
            .then((env) => {
              if (!env.data) return;
              setRecord(env.data);
              setValues(valuesFrom(env.data));
              setSaved("Saved.");
            })
            .catch((err) => setError(errorMessage(err)))
            .finally(() => setSaving(false));
        }}
      >
        <p className="al-account-contact-title">My Contact Information</p>
        {error ? <p className="al-account-contact-note">{error}</p> : null}
        {loading || !record ? (
          loading ? (
            <div className="al-account-contact-loading" role="status" aria-label="Loading contact">
              <span className="al-account-contact-spinner" aria-hidden="true" />
            </div>
          ) : null
        ) : (
          <>
            <ContactFields
              values={values}
              disabled={saving}
              locked={(id) => loginFieldLocked(record, id, capabilities)}
              onChange={(id, value) => {
                setSaved("");
                setValues((current) => ({ ...current, [id]: value }));
              }}
            />
            {loginLocked ? (
              <p className="al-account-contact-note">
                A sign-in email or phone that already has a value stays as it is.
              </p>
            ) : null}
            {saved ? <p className="al-account-contact-note">{saved}</p> : null}
          </>
        )}
      </form>
      {open && dirty ? (
        <button type="submit" form="al-own-contact" className="al-account-contact-save" disabled={loading || saving}>
          {saving ? "Saving" : "Save"}
        </button>
      ) : null}
    </div>
  );
}
